import React, { useState, useEffect } from 'react';
import { AutomationWebhook, AutomationWebhookCreate, AutomationMappingPreset } from '../types/automation';
import AutomationPresetPicker from './AutomationPresetPicker';
import { MappingPresetTemplate } from '../types/automation';
import { api } from '../lib/api';
import ZapierSetupGuide from './ZapierSetupGuide';

interface AutomationWebhookConfigProps {
  webhook?: AutomationWebhook;
  onSave: (webhookData: AutomationWebhookCreate) => void;
  onCancel: () => void;
  tenantId: string;
}

const AutomationWebhookConfig: React.FC<AutomationWebhookConfigProps> = ({
  webhook,
  onSave,
  onCancel,
  tenantId
}) => {
  const [formData, setFormData] = useState({
    name: webhook?.name || '',
    webhook_type: webhook?.webhook_type || 'zapier',
    is_active: webhook?.is_active ?? true,
    mapping_preset_id: webhook?.mapping_preset_id || '',
    auto_enqueue_campaign_id: webhook?.auto_enqueue_campaign_id || '',
    enqueue_delay_minutes: webhook?.enqueue_delay_minutes || 0,
    metadata: webhook?.metadata || {}
  });

  const [selectedPreset, setSelectedPreset] = useState<MappingPresetTemplate | null>(null);
  const [selectedDatabasePresetId, setSelectedDatabasePresetId] = useState<string | null>(
    webhook?.mapping_preset_id || null
  );
  const [customMapping, setCustomMapping] = useState<Record<string, string>>(webhook?.custom_mapping || {});
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [presets, setPresets] = useState<AutomationMappingPreset[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [useCustomMapping, setUseCustomMapping] = useState<boolean>(
    !webhook?.mapping_preset_id && Object.keys(webhook?.custom_mapping || {}).length > 0
  );

  // System presets (same as in AutomationPresetPicker)
  const systemPresets: MappingPresetTemplate[] = [
    {
      name: "Dental Practice - Basic",
      description: "Basic field mapping for dental practice management systems",
      niche: "dental",
      field_mappings: [
        { source_field: "patient_email", target_field: "email", required: true },
        { source_field: "patient_phone", target_field: "phone" },
        { source_field: "first_name", target_field: "first_name", required: true },
        { source_field: "last_name", target_field: "last_name", required: true },
        { source_field: "appointment_date", target_field: "custom_fields.appointment_date" },
        { source_field: "service_type", target_field: "custom_fields.service_type" },
        { source_field: "provider_name", target_field: "custom_fields.provider_name" },
        { source_field: "insurance_provider", target_field: "custom_fields.insurance_provider" },
        { source_field: "patient_id", target_field: "custom_fields.patient_id" },
      ],
      required_fields: ["email", "first_name", "last_name"],
      optional_fields: ["phone", "appointment_date", "service_type", "provider_name", "insurance_provider", "patient_id"],
      validation_rules: { email_format: true, phone_format: "us" },
      sample_data: {
        patient_email: "john.doe@example.com",
        patient_phone: "+1234567890",
        first_name: "John",
        last_name: "Doe",
        appointment_date: "2024-01-15",
        service_type: "Cleaning",
        provider_name: "Dr. Smith",
        insurance_provider: "Blue Cross",
        patient_id: "PAT123456"
      }
    },
    {
      name: "Salon/Spa - Basic",
      description: "Basic field mapping for salon and spa management systems",
      niche: "salon",
      field_mappings: [
        { source_field: "client_email", target_field: "email", required: true },
        { source_field: "client_phone", target_field: "phone" },
        { source_field: "first_name", target_field: "first_name", required: true },
        { source_field: "last_name", target_field: "last_name", required: true },
        { source_field: "appointment_date", target_field: "custom_fields.appointment_date" },
        { source_field: "service_name", target_field: "custom_fields.service_name" },
        { source_field: "stylist_name", target_field: "custom_fields.stylist_name" },
        { source_field: "total_amount", target_field: "custom_fields.total_amount" },
        { source_field: "client_id", target_field: "custom_fields.client_id" },
        { source_field: "notes", target_field: "custom_fields.notes" },
      ],
      required_fields: ["email", "first_name", "last_name"],
      optional_fields: ["phone", "appointment_date", "service_name", "stylist_name", "total_amount", "client_id", "notes"],
      validation_rules: { email_format: true, phone_format: "us" },
      sample_data: {
        client_email: "jane.smith@example.com",
        client_phone: "+1234567890",
        first_name: "Jane",
        last_name: "Smith",
        appointment_date: "2024-01-15",
        service_name: "Haircut & Color",
        stylist_name: "Sarah Johnson",
        total_amount: "150.00",
        client_id: "CLI789012",
        notes: "First time client, wants highlights"
      }
    },
    {
      name: "Restaurant - Basic",
      description: "Basic field mapping for restaurant reservation systems",
      niche: "restaurant",
      field_mappings: [
        { source_field: "customer_email", target_field: "email", required: true },
        { source_field: "customer_phone", target_field: "phone" },
        { source_field: "first_name", target_field: "first_name", required: true },
        { source_field: "last_name", target_field: "last_name", required: true },
        { source_field: "reservation_date", target_field: "custom_fields.reservation_date" },
        { source_field: "party_size", target_field: "custom_fields.party_size" },
        { source_field: "special_requests", target_field: "custom_fields.special_requests" },
        { source_field: "table_preference", target_field: "custom_fields.table_preference" },
        { source_field: "customer_id", target_field: "custom_fields.customer_id" },
      ],
      required_fields: ["email", "first_name", "last_name"],
      optional_fields: ["phone", "reservation_date", "party_size", "special_requests", "table_preference", "customer_id"],
      validation_rules: { email_format: true, phone_format: "us" },
      sample_data: {
        customer_email: "mike.wilson@example.com",
        customer_phone: "+1234567890",
        first_name: "Mike",
        last_name: "Wilson",
        reservation_date: "2024-01-15 19:00",
        party_size: "4",
        special_requests: "Birthday celebration",
        table_preference: "Window table",
        customer_id: "CUST345678"
      }
    },
    {
      name: "Veterinary Practice - Basic",
      description: "Basic field mapping for veterinary practice management systems",
      niche: "veterinary",
      field_mappings: [
        { source_field: "owner_email", target_field: "email", required: true },
        { source_field: "owner_phone", target_field: "phone" },
        { source_field: "first_name", target_field: "first_name", required: true },
        { source_field: "last_name", target_field: "last_name", required: true },
        { source_field: "pet_name", target_field: "custom_fields.pet_name" },
        { source_field: "pet_species", target_field: "custom_fields.pet_species" },
        { source_field: "pet_breed", target_field: "custom_fields.pet_breed" },
        { source_field: "appointment_date", target_field: "custom_fields.appointment_date" },
        { source_field: "procedure_name", target_field: "custom_fields.procedure_name" },
        { source_field: "doctor_name", target_field: "custom_fields.doctor_name" },
        { source_field: "client_id", target_field: "custom_fields.client_id" },
      ],
      required_fields: ["email", "first_name", "last_name"],
      optional_fields: ["phone", "pet_name", "pet_species", "pet_breed", "appointment_date", "procedure_name", "doctor_name", "client_id"],
      validation_rules: { email_format: true, phone_format: "us" },
      sample_data: {
        owner_email: "jennifer.brown@example.com",
        owner_phone: "+1234567890",
        first_name: "Jennifer",
        last_name: "Brown",
        pet_name: "Buddy",
        pet_species: "Dog",
        pet_breed: "Golden Retriever",
        appointment_date: "2024-01-15",
        procedure_name: "Dental Surgery",
        doctor_name: "Dr. Smith",
        client_id: "CLI123456"
      }
    },
    {
      name: "Medical Practice - Basic",
      description: "Basic field mapping for medical practice management systems",
      niche: "medical",
      field_mappings: [
        { source_field: "patient_email", target_field: "email", required: true },
        { source_field: "patient_phone", target_field: "phone" },
        { source_field: "first_name", target_field: "first_name", required: true },
        { source_field: "last_name", target_field: "last_name", required: true },
        { source_field: "appointment_date", target_field: "custom_fields.appointment_date" },
        { source_field: "service_type", target_field: "custom_fields.service_type" },
        { source_field: "provider_name", target_field: "custom_fields.provider_name" },
        { source_field: "insurance_provider", target_field: "custom_fields.insurance_provider" },
        { source_field: "patient_id", target_field: "custom_fields.patient_id" },
        { source_field: "date_of_birth", target_field: "custom_fields.date_of_birth" },
      ],
      required_fields: ["email", "first_name", "last_name"],
      optional_fields: ["phone", "appointment_date", "service_type", "provider_name", "insurance_provider", "patient_id", "date_of_birth"],
      validation_rules: { email_format: true, phone_format: "us" },
      sample_data: {
        patient_email: "john.doe@example.com",
        patient_phone: "+1234567890",
        first_name: "John",
        last_name: "Doe",
        appointment_date: "2024-01-15",
        service_type: "Annual Checkup",
        provider_name: "Dr. Johnson",
        insurance_provider: "Blue Cross",
        patient_id: "PAT789012",
        date_of_birth: "1980-05-15"
      }
    },
    {
      name: "General Business - Basic",
      description: "Basic field mapping for general business customer management",
      niche: "general",
      field_mappings: [
        { source_field: "email", target_field: "email", required: true },
        { source_field: "phone", target_field: "phone" },
        { source_field: "first_name", target_field: "first_name", required: true },
        { source_field: "last_name", target_field: "last_name", required: true },
        { source_field: "company", target_field: "company" },
        { source_field: "job_title", target_field: "custom_fields.job_title" },
        { source_field: "industry", target_field: "custom_fields.industry" },
        { source_field: "lead_source", target_field: "custom_fields.lead_source" },
      ],
      required_fields: ["email", "first_name", "last_name"],
      optional_fields: ["phone", "company", "job_title", "industry", "lead_source"],
      validation_rules: { email_format: true, phone_format: "us" },
      sample_data: {
        email: "contact@example.com",
        phone: "+1234567890",
        first_name: "Jane",
        last_name: "Smith",
        company: "Example Corp",
        job_title: "Marketing Manager",
        industry: "Technology",
        lead_source: "Website"
      }
    },
    {
      name: "E-commerce - Basic",
      description: "Basic field mapping for e-commerce customer management",
      niche: "ecommerce",
      field_mappings: [
        { source_field: "email", target_field: "email", required: true },
        { source_field: "phone", target_field: "phone" },
        { source_field: "first_name", target_field: "first_name", required: true },
        { source_field: "last_name", target_field: "last_name", required: true },
        { source_field: "company", target_field: "company" },
        { source_field: "order_id", target_field: "custom_fields.order_id" },
        { source_field: "order_date", target_field: "custom_fields.order_date" },
        { source_field: "order_total", target_field: "custom_fields.order_total" },
        { source_field: "product_name", target_field: "custom_fields.product_name" },
      ],
      required_fields: ["email", "first_name", "last_name"],
      optional_fields: ["phone", "company", "order_id", "order_date", "order_total", "product_name"],
      validation_rules: { email_format: true, phone_format: "us" },
      sample_data: {
        email: "customer@example.com",
        phone: "+1234567890",
        first_name: "Mike",
        last_name: "Wilson",
        company: "Retail Store",
        order_id: "ORD123456",
        order_date: "2024-01-15",
        order_total: "99.99",
        product_name: "Product XYZ"
      }
    },
    {
      name: "Service Business - Basic",
      description: "Basic field mapping for service-based businesses",
      niche: "service",
      field_mappings: [
        { source_field: "email", target_field: "email", required: true },
        { source_field: "phone", target_field: "phone" },
        { source_field: "first_name", target_field: "first_name", required: true },
        { source_field: "last_name", target_field: "last_name", required: true },
        { source_field: "company", target_field: "company" },
        { source_field: "service_date", target_field: "custom_fields.service_date" },
        { source_field: "service_type", target_field: "custom_fields.service_type" },
        { source_field: "service_provider", target_field: "custom_fields.service_provider" },
        { source_field: "client_id", target_field: "custom_fields.client_id" },
      ],
      required_fields: ["email", "first_name", "last_name"],
      optional_fields: ["phone", "company", "service_date", "service_type", "service_provider", "client_id"],
      validation_rules: { email_format: true, phone_format: "us" },
      sample_data: {
        email: "client@example.com",
        phone: "+1234567890",
        first_name: "Sarah",
        last_name: "Johnson",
        company: "Business Inc",
        service_date: "2024-01-15",
        service_type: "Consulting",
        service_provider: "John Consultant",
        client_id: "CLI456789"
      }
    }
  ];

  useEffect(() => {
    // Load campaigns and presets for the tenant
    loadCampaigns();
    loadPresets();
  }, [tenantId]);

  // Initialize selected preset when editing existing webhook
  useEffect(() => {
    if (webhook) {
      // If webhook has mapping_preset_id, use that (database preset)
      if (webhook.mapping_preset_id) {
        setSelectedDatabasePresetId(webhook.mapping_preset_id);
        setUseCustomMapping(false);
        setCustomMapping({});
        setSelectedPreset(null);
      }
      // If webhook has custom_mapping, try to match to system preset
      else if (webhook.custom_mapping && Object.keys(webhook.custom_mapping).length > 0) {
        setUseCustomMapping(true);
        // Try to match existing custom_mapping to a system preset
        const existingMapping = webhook.custom_mapping;
        
        for (const preset of systemPresets) {
          // Convert preset field_mappings to a Record for comparison
          const presetMapping: Record<string, string> = {};
          preset.field_mappings.forEach(mapping => {
            presetMapping[mapping.source_field] = mapping.target_field;
          });
          
          // Check if all preset mappings exist in the custom mapping
          const presetKeys = Object.keys(presetMapping);
          const allMatch = presetKeys.every(key => {
            return existingMapping[key] === presetMapping[key];
          });
          
          // If all mappings match, this is likely the preset that was used
          if (allMatch && presetKeys.length === Object.keys(existingMapping).length) {
            setSelectedPreset(preset);
            break;
          }
        }
      }
    }
  }, [webhook]);

  const loadCampaigns = async () => {
    try {
      const response = await api.campaigns.list();
      const data = response.data;
      // Ensure we always set an array
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      setCampaigns([]);
    }
  };

  const loadPresets = async () => {
    try {
      const response = await api.automation.getTenantPresets(tenantId);
      setPresets(response.data || []);
    } catch (error) {
      console.error('Error loading presets:', error);
      setPresets([]);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handlePresetSelect = (preset: MappingPresetTemplate) => {
    // System template selected - convert to custom_mapping
    setSelectedPreset(preset);
    setSelectedDatabasePresetId(null);
    setUseCustomMapping(true);
    // Convert template to mapping preset format
    const fieldMappings: Record<string, string> = {};
    preset.field_mappings.forEach(mapping => {
      fieldMappings[mapping.source_field] = mapping.target_field;
    });
    setCustomMapping(fieldMappings);
    // Clear mapping_preset_id in formData
    setFormData(prev => ({ ...prev, mapping_preset_id: '' }));
  };

  const handleDatabasePresetSelect = (presetId: string) => {
    // Database preset selected - use mapping_preset_id
    setSelectedDatabasePresetId(presetId);
    setSelectedPreset(null);
    setUseCustomMapping(false);
    setCustomMapping({});
    // Set mapping_preset_id in formData
    setFormData(prev => ({ ...prev, mapping_preset_id: presetId }));
  };

  const handleCustomMappingChange = (mapping: Record<string, string>) => {
    setCustomMapping(mapping);
    // If custom mapping is set, clear database preset selection
    if (Object.keys(mapping).length > 0) {
      setSelectedDatabasePresetId(null);
      setFormData(prev => ({ ...prev, mapping_preset_id: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Webhook name is required';
    }

    if (!formData.webhook_type) {
      newErrors.webhook_type = 'Webhook type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Determine which mapping to use: custom_mapping or mapping_preset_id
    const hasCustomMapping = Object.keys(customMapping).length > 0;
    const hasDatabasePreset = selectedDatabasePresetId || formData.mapping_preset_id;
    
    const webhookData: AutomationWebhookCreate = {
      tenant_id: tenantId,
      name: formData.name,
      webhook_type: formData.webhook_type,
      is_active: formData.is_active,
      // Use mapping_preset_id if database preset is selected, otherwise use custom_mapping
      mapping_preset_id: hasCustomMapping ? undefined : (hasDatabasePreset || undefined),
      custom_mapping: hasCustomMapping ? customMapping : undefined,
      auto_enqueue_campaign_id: formData.auto_enqueue_campaign_id || undefined,
      enqueue_delay_minutes: formData.enqueue_delay_minutes,
      metadata: formData.metadata
    };

    onSave(webhookData);
  };

  // Generate test data based on custom mapping
  const getTestDataForGuide = () => {
    if (!webhook) return undefined;
    
    const mapping = webhook.custom_mapping || {};
    
    // If we have a selected preset, use its sample_data (source fields with examples)
    if (selectedPreset && selectedPreset.sample_data) {
      return { data: selectedPreset.sample_data };
    }
    
    // If we have a database preset, try to get its sample data from metadata or generate from field_mappings
    if (selectedDatabasePresetId) {
      const dbPreset = presets.find(p => p.id === selectedDatabasePresetId);
      if (dbPreset) {
        // Check if sample_data is in metadata
        if (dbPreset.metadata && dbPreset.metadata.sample_data) {
          return { data: dbPreset.metadata.sample_data };
        }
        // Otherwise, generate from field_mappings (field_mappings keys are source fields)
        if (dbPreset.field_mappings && Object.keys(dbPreset.field_mappings).length > 0) {
          const sampleData: any = {};
          Object.keys(dbPreset.field_mappings).forEach(sourceField => {
            const lowerField = sourceField.toLowerCase();
            if (lowerField.includes('email')) {
              sampleData[sourceField] = "customer@example.com";
            } else if (lowerField.includes('phone')) {
              sampleData[sourceField] = "+1234567890";
            } else if (lowerField.includes('first')) {
              sampleData[sourceField] = "Jane";
            } else if (lowerField.includes('last')) {
              sampleData[sourceField] = "Smith";
            } else if (lowerField.includes('date')) {
              sampleData[sourceField] = "2024-01-15";
            } else if (lowerField.includes('name') && !lowerField.includes('first') && !lowerField.includes('last')) {
              sampleData[sourceField] = "Example Name";
            } else if (lowerField.includes('id')) {
              sampleData[sourceField] = "ID123456";
            } else if (lowerField.includes('amount') || lowerField.includes('price') || lowerField.includes('total')) {
              sampleData[sourceField] = "100.00";
            } else if (lowerField.includes('type') || lowerField.includes('service')) {
              sampleData[sourceField] = "Example Service";
            } else if (lowerField.includes('provider') || lowerField.includes('doctor') || lowerField.includes('stylist')) {
              sampleData[sourceField] = "Dr. Example";
            } else if (lowerField.includes('insurance')) {
              sampleData[sourceField] = "Example Insurance";
            } else if (lowerField.includes('company')) {
              sampleData[sourceField] = "Example Corp";
            } else if (lowerField.includes('notes') || lowerField.includes('note')) {
              sampleData[sourceField] = "Example notes";
            } else {
              sampleData[sourceField] = `example_${sourceField}`;
            }
          });
          return { data: sampleData };
        }
      }
    }
    
    // Generate sample data from custom_mapping source fields (keys are source field names)
    const sampleData: any = {};
    
    if (Object.keys(mapping).length === 0) {
      // No mapping - use defaults with common source field names
      sampleData.email = "customer@example.com";
      sampleData.first_name = "Jane";
      sampleData.last_name = "Smith";
      sampleData.phone = "+1234567890";
    } else {
      // Use source field names from custom_mapping keys (these are what users enter in Zapier)
      Object.keys(mapping).forEach(sourceField => {
        // Generate example value based on source field name
        const lowerField = sourceField.toLowerCase();
        if (lowerField.includes('email')) {
          sampleData[sourceField] = "customer@example.com";
        } else if (lowerField.includes('phone')) {
          sampleData[sourceField] = "+1234567890";
        } else if (lowerField.includes('first')) {
          sampleData[sourceField] = "Jane";
        } else if (lowerField.includes('last')) {
          sampleData[sourceField] = "Smith";
        } else if (lowerField.includes('date')) {
          sampleData[sourceField] = "2024-01-15";
        } else if (lowerField.includes('name') && !lowerField.includes('first') && !lowerField.includes('last')) {
          sampleData[sourceField] = "Example Name";
        } else if (lowerField.includes('id')) {
          sampleData[sourceField] = "ID123456";
        } else if (lowerField.includes('amount') || lowerField.includes('price') || lowerField.includes('total')) {
          sampleData[sourceField] = "100.00";
        } else if (lowerField.includes('type') || lowerField.includes('service')) {
          sampleData[sourceField] = "Example Service";
        } else if (lowerField.includes('provider') || lowerField.includes('doctor') || lowerField.includes('stylist')) {
          sampleData[sourceField] = "Dr. Example";
        } else if (lowerField.includes('insurance')) {
          sampleData[sourceField] = "Example Insurance";
        } else if (lowerField.includes('company')) {
          sampleData[sourceField] = "Example Corp";
        } else if (lowerField.includes('notes') || lowerField.includes('note')) {
          sampleData[sourceField] = "Example notes";
        } else {
          sampleData[sourceField] = `example_${sourceField}`;
        }
      });
    }
    
    return { data: sampleData };
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {webhook ? 'Edit Webhook' : 'Create New Webhook'}
        </h2>
        <p className="text-gray-600 mt-1">
          Configure automation webhook to receive data from external systems
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Configuration */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Configuration</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Webhook Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="e.g., Zapier Dental Patients"
              />
              {errors.name && <p className="text-red-600 text-sm mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Webhook Type *
              </label>
              <select
                value={formData.webhook_type}
                onChange={(e) => handleInputChange('webhook_type', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md ${
                  errors.webhook_type ? 'border-red-300' : 'border-gray-300'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              >
                <option value="zapier">Zapier</option>
                <option value="make">Make (Integromat)</option>
                <option value="custom">Custom</option>
              </select>
              {errors.webhook_type && <p className="text-red-600 text-sm mt-1">{errors.webhook_type}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={formData.is_active.toString()}
                onChange={(e) => handleInputChange('is_active', e.target.value === 'true')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Auto-enqueue Campaign
              </label>
              <select
                value={formData.auto_enqueue_campaign_id}
                onChange={(e) => handleInputChange('auto_enqueue_campaign_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">No auto-enqueue</option>
                {campaigns.map(campaign => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enqueue Delay (minutes)
              </label>
              <input
                type="number"
                min="0"
                value={formData.enqueue_delay_minutes}
                onChange={(e) => handleInputChange('enqueue_delay_minutes', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-sm text-gray-500 mt-1">
                Delay before automatically enqueuing the selected campaign
              </p>
            </div>
          </div>
        </div>

        {/* Field Mapping Configuration */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Field Mapping Configuration</h3>
          
          <AutomationPresetPicker
            onPresetSelect={handlePresetSelect}
            onDatabasePresetSelect={handleDatabasePresetSelect}
            onCustomMapping={handleCustomMappingChange}
            selectedPreset={selectedPreset}
            selectedDatabasePresetId={selectedDatabasePresetId}
            customMapping={customMapping}
            tenantPresets={presets}
          />
        </div>

        {/* Zapier Setup Guide - Always visible with toggle, positioned above Webhook URL */}
        {webhook && (
          <ZapierSetupGuide 
            webhook={webhook}
            testData={getTestDataForGuide()}
          />
        )}

        {/* Webhook URL Display */}
        {webhook && (
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Webhook URL</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Webhook URL
                </label>
                <div className="flex">
                  <input
                    type="text"
                    value={(() => {
                      const hostname = window.location.hostname.toLowerCase();
                      const defaultApiUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.feedbackgate.com';
                      
                      // If on app.feedbackgate.com, convert to api.feedbackgate.com
                      if (hostname === 'app.feedbackgate.com' || hostname.startsWith('app.')) {
                        const protocol = window.location.protocol;
                        const apiHost = hostname.replace(/^app\./, 'api.');
                        return `${protocol}//${apiHost}/api/automation/hooks/zap`;
                      }
                      
                      // If on custom domain, use same domain for API
                      if (hostname && 
                          !hostname.includes('feedbackgate.com') && 
                          !hostname.includes('localhost') && 
                          !hostname.includes('127.0.0.1')) {
                        const protocol = window.location.protocol;
                        if (hostname.startsWith('www.')) {
                          const apiHost = hostname.replace(/^www\./, 'api.');
                          return `${protocol}//${apiHost}/api/automation/hooks/zap`;
                        } else {
                          return `${protocol}//${hostname}/api/automation/hooks/zap`;
                        }
                      }
                      
                      return `${defaultApiUrl}/api/automation/hooks/zap`;
                    })()}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md bg-gray-50 text-gray-600"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const hostname = window.location.hostname.toLowerCase();
                      const defaultApiUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.feedbackgate.com';
                      let webhookUrl;
                      
                      // If on app.feedbackgate.com, convert to api.feedbackgate.com
                      if (hostname === 'app.feedbackgate.com' || hostname.startsWith('app.')) {
                        const protocol = window.location.protocol;
                        const apiHost = hostname.replace(/^app\./, 'api.');
                        webhookUrl = `${protocol}//${apiHost}/api/automation/hooks/zap`;
                      } else if (hostname && 
                          !hostname.includes('feedbackgate.com') && 
                          !hostname.includes('localhost') && 
                          !hostname.includes('127.0.0.1')) {
                        const protocol = window.location.protocol;
                        if (hostname.startsWith('www.')) {
                          const apiHost = hostname.replace(/^www\./, 'api.');
                          webhookUrl = `${protocol}//${apiHost}/api/automation/hooks/zap`;
                        } else {
                          webhookUrl = `${protocol}//${hostname}/api/automation/hooks/zap`;
                        }
                      } else {
                        webhookUrl = `${defaultApiUrl}/api/automation/hooks/zap`;
                      }
                      navigator.clipboard.writeText(webhookUrl);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700"
                  >
                    Copy
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Secret Key
                </label>
                <div className="flex">
                  <input
                    type="password"
                    value={webhook.secret_key}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md bg-gray-50 text-gray-600"
                  />
                  <button
                    type="button"
                    onClick={() => navigator.clipboard.writeText(webhook.secret_key)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700"
                  >
                    Copy
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Use this secret key in the X-Webhook-Secret header
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            {webhook ? 'Update Webhook' : 'Create Webhook'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AutomationWebhookConfig;
